import {
    FC,
    ReactElement,
    SubmitEvent,
    useCallback,
    useState,
} from 'react'
import Cropper, { Area, Point } from 'react-easy-crop'

import { FormActionButtons, ModalContent, PacoModal } from '@/compositions'
import { cropImage } from '@/helpers/cropImage'
import { trans } from '@/helpers/trans'

import './AvatarCropModal.scss'

const MIN_ZOOM = 1
const MAX_ZOOM = 3

interface AvatarCropModalProps {
    imageSrc: string
    isSaving: boolean
    onCancel: () => void
    onConfirm: (avatar: Blob) => void
}

export const AvatarCropModal: FC<AvatarCropModalProps> = ({
    imageSrc,
    isSaving,
    onCancel,
    onConfirm,
}): ReactElement => {
    const [crop, setCrop] = useState<Point>({ x: 0, y: 0 })
    const [zoom, setZoom] = useState(MIN_ZOOM)
    const [croppedArea, setCroppedArea] = useState<Area>()

    const handleCropComplete = useCallback((_: Area, croppedAreaPixels: Area): void => {
        setCroppedArea(croppedAreaPixels)
    }, [])

    const handleSubmit = async (event: SubmitEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (! croppedArea) {
            return
        }

        try {
            onConfirm(await cropImage(imageSrc, croppedArea))
        } catch (error) {
            console.error('[AvatarCropModal]', error)
        }
    }

    return (
        <PacoModal cardClassName="avatar-crop-modal">
            <ModalContent title={trans('pages.profile.avatar.cropTitle')}>
                <form onSubmit={handleSubmit}>
                    <div className="avatar-crop-modal__cropper">
                        <Cropper
                            image={imageSrc}
                            crop={crop}
                            zoom={zoom}
                            minZoom={MIN_ZOOM}
                            maxZoom={MAX_ZOOM}
                            aspect={1}
                            cropShape="round"
                            showGrid={false}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={handleCropComplete}
                        />
                    </div>

                    <label className="avatar-crop-modal__zoom">
                        <span>{trans('pages.profile.avatar.zoom')}</span>
                        <input
                            type="range"
                            min={MIN_ZOOM}
                            max={MAX_ZOOM}
                            step={0.01}
                            value={zoom}
                            onChange={event => setZoom(Number(event.target.value))}
                            className="avatar-crop-modal__zoom-input"
                        />
                    </label>

                    <FormActionButtons
                        isLoading={isSaving}
                        disabled={! croppedArea || isSaving}
                        onCancelClick={onCancel}
                        className="avatar-crop-modal__form-action-buttons"
                    />
                </form>
            </ModalContent>
        </PacoModal>
    )
}
